'use client';

import { useEffect } from 'react';

import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Container,
    IconName,
    Justify,
    Price,
    PriceLevel,
    Rows,
    Text,
    TextLevels,
    TextMarkup,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { ConfigType } from '@app-types/api';
import { RepeaterProduct } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';

import useAbTest from '@hooks/use-abtest';

import { PHONE_AB_TEST, VariationEnum } from '@helpers/abtest-utils';
import { calculFinalPrice, formatPrice, roundDecimal } from '@helpers/utils';

import { useHome } from '@landing/hooks';

import StickyItem from './sticky-item';

type StickyProps = Readonly<{
    mobileFirst: boolean;
    isStickyVisible: boolean;
    repeaters?: { [key: string]: RepeaterProduct };
    config?: ConfigType;
    isLoading: boolean;
}>;

export default function StickySummary({
    mobileFirst,
    isStickyVisible,
    repeaters,
    config,
    isLoading,
}: Readonly<StickyProps>) {
    const { selectedFaiOption, selectedFaiProduct, selectedPlan, handleAddToCart, selectedNetwork } = useHome();
    const repeater = selectedFaiProduct?.gencode_offre ? repeaters?.[selectedFaiProduct.gencode_offre] : undefined;
    const bboxPrice = calculFinalPrice(selectedNetwork, selectedFaiProduct, null);
    const planPrice = calculFinalPrice(selectedNetwork, selectedPlan, selectedFaiProduct);
    const otisRepeaterPrice = selectedFaiOption && repeater ? repeater.prices.final : 0;

    useEffect(() => {
        // Position omnikit widget just before the sticky bar
        window.omnikit = { insertBeforeId: 'sticky-footer' };
    }, []);

    const { abTestVariant: abTestPhoneVariant } = useAbTest(
        PHONE_AB_TEST,
        config?.ab_test_boost_taux_ouverture,
        'step_phone',
        false,
    );

    const totalPrice = roundDecimal(bboxPrice.now + planPrice?.now + otisRepeaterPrice);
    const foreverPrice = planPrice?.forever !== planPrice?.now ? planPrice?.forever : undefined;

    const repeaterTitle = selectedFaiProduct?.title_repeater ?? 'Répéteur WiFi';
    const promoLabel = repeater?.promoLabel ? `(${repeater.promoLabel}) ` : '';
    const secondLineFai = `${repeaterTitle} : ${promoLabel}${repeater?.prices.final} €/mois`;
    const secondLineFaiMobile = `${repeaterTitle} ${promoLabel}`;

    return (
        <footer
            className={`is-sticky-bottom sticky-bottom is-flex${isStickyVisible ? ' visible' : ''}`}
            id='sticky-footer'
            style={{
                marginTop: '30px',
            }}
        >
            <Container className='is-marginless-mobile is-fullwidth-mobile' medium>
                <Box className='is-fullwidth'>
                    <BoxContent>
                        <Columns verticalAlign={Align.CENTER}>
                            <Column>
                                <Columns verticalAlign={Align.CENTER}>
                                    <Column narrow>
                                        <View className='is-flex-mobile' justify={Justify.SPACE_BETWEEN}>
                                            <Text marginless typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                                Votre sélection
                                            </Text>
                                            <div>
                                                <View className='is-hidden-tablet'>
                                                    <InlinePrice amount={totalPrice} period='mois' />
                                                </View>
                                                <View className='is-hidden-mobile' style={{ marginTop: '4px' }}>
                                                    <Price
                                                        level={PriceLevel.FIVE}
                                                        align={Align.END}
                                                        amount={totalPrice}
                                                        period='mois'
                                                    />
                                                </View>
                                            </div>
                                        </View>
                                    </Column>
                                    <Column>
                                        <Rows gap={1} className='is-flex'>
                                            <StickyItem
                                                iconName={IconName.SIM_CARD}
                                                offerType={selectedPlan?.name ?? 'Forfait mobile'}
                                                price={planPrice?.now}
                                                noOffer={{
                                                    text: '+ Ajouter un forfait mobile',
                                                    id: 'plans-configurator',
                                                }}
                                                order={mobileFirst ? 0 : 2}
                                                isLoading={isLoading}
                                                secondLine={
                                                    foreverPrice && planPrice.duration ? (
                                                        <Text markup={TextMarkup.SPAN} level={TextLevels.THREE}>
                                                            Pendant {planPrice.duration} mois puis{' '}
                                                            {formatPrice(foreverPrice)} /mois
                                                        </Text>
                                                    ) : null
                                                }
                                            />

                                            <StickyItem
                                                iconName={IconName.BOX}
                                                offerType={selectedFaiProduct?.title ?? 'Offre fibre'}
                                                price={bboxPrice.now}
                                                noOffer={{
                                                    text: '+ Ajouter une offre fibre',
                                                    id: 'bbox-configurator',
                                                }}
                                                order={mobileFirst ? 1 : 0}
                                                isLoading={isLoading}
                                                secondLine={
                                                    selectedFaiOption && selectedFaiProduct ? (
                                                        <Text
                                                            className='is-hidden-mobile'
                                                            markup={TextMarkup.SPAN}
                                                            level={TextLevels.THREE}
                                                        >
                                                            {secondLineFai}
                                                        </Text>
                                                    ) : null
                                                }
                                            />
                                            {selectedFaiOption && selectedFaiProduct && (
                                                <View
                                                    className='is-hidden-tablet'
                                                    style={{ order: mobileFirst ? 2 : 1 }}
                                                >
                                                    <StickyItem
                                                        iconName={IconName.BOX}
                                                        offerType={secondLineFaiMobile}
                                                        price={repeater?.prices.final}
                                                        isLoading={isLoading}
                                                    />
                                                </View>
                                            )}
                                        </Rows>
                                    </Column>
                                </Columns>
                            </Column>
                            <Column narrow>
                                <Button
                                    data-testid='sticky-btn'
                                    onClick={() => {
                                        handleAddToCart({
                                            isPhoneStepEnabled: abTestPhoneVariant === VariationEnum.VARIATION_A,
                                        });
                                    }}
                                    variant={ButtonVariant.CONVERSION}
                                >
                                    Continuer
                                </Button>
                            </Column>
                        </Columns>
                    </BoxContent>
                </Box>
            </Container>
        </footer>
    );
}
